import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useEffect, useState } from "react";
import moment from "moment";
import { Box } from "@mui/material";
import { IconButton, InputAdornment } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import EventIcon from "@mui/icons-material/Event";

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (date: Date | null) => void;
  onEndDateChange: (date: Date | null) => void;
  onDateRangeChange?: (dateRange: {
    startDate: string | null;
    endDate: string | null;
  }) => void;
  startMaxDate?: Date | any;
  endMaxDate?: Date | any;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onDateRangeChange,
  startMaxDate,
  endMaxDate
}) => {
  const [startPickerOpen, setStartPickerOpen] = useState(false);
  const [endPickerOpen, setEndPickerOpen] = useState(false);

  const handleStartDateChange = (value: Date | null) => {
    onStartDateChange(value);

    if (onDateRangeChange) {
      const formattedStartDate = value
        ? moment(value).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = endDate
        ? moment(endDate).format("YYYY-MM-DD")
        : "";

      onDateRangeChange({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
  };

  const handleEndDateChange = (value: Date | null) => {
    onEndDateChange(value);

    if (onDateRangeChange) {
      const formattedStartDate = startDate
        ? moment(startDate).format("YYYY-MM-DD")
        : "";
      const formattedEndDate = value ? moment(value).format("YYYY-MM-DD") : "";

      onDateRangeChange({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {/* <Box display="flex" justifyContent="end" gap={2} flexWrap="wrap"> */}
      <Box
        sx={{
          display: "flex",
          flexWrap: { xs: "wrap", sm: "unset", md: "unset" },
          justifyContent: { xs: "start", sm: "end", md: "end" },
        }}
        gap={2}
      >
        {/* <Box display="flex" justifyContent={"end"} gap={2}> */}
        {/* start Date Picker */}
        <DatePicker
          label="Start Date"
          value={startDate}
          onChange={handleStartDateChange}
          maxDate={endDate ?? undefined}
          format="dd-MM-yyyy"
          open={startPickerOpen}
          onClose={() => setStartPickerOpen(false)}
          {...(startMaxDate &&{maxDate:startMaxDate})}
          closeOnSelect={true}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: { xs: "45%", sm: "200px", md: "200px" },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  height: "40px",
                },
              },
              onClick: () => setStartPickerOpen(true),
              InputProps: startDate
                ? {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {/* Clear icon if date is selected */}
                        {startDate && (
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleStartDateChange(null);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }
                : {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setStartPickerOpen(true);
                          }}
                        >
                          <EventIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
            },
            field: {
              readOnly: true,
            },
          }}
        />

        {/* End Date Picker */}
        <DatePicker
          label="End Date"
          value={endDate}
          onChange={handleEndDateChange}
          minDate={startDate ?? undefined}
          {...(endMaxDate && {maxDate:endMaxDate})}
          format="dd-MM-yyyy"
          open={endPickerOpen}
          onClose={() => setEndPickerOpen(false)}
          closeOnSelect={true}
          slotProps={{
            textField: {
              size: "small",
              sx: {
                width: { xs: "45%", sm: "200px", md: "200px" },
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  height: "40px",
                },
              },
              onClick: () => setEndPickerOpen(true),
              InputProps: endDate
                ? {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {endDate && (
                          <IconButton
                            edge="end"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEndDateChange(null);
                            }}
                          >
                            <ClearIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }
                : {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEndPickerOpen(true);
                          }}
                        >
                          <EventIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
            },
            field: {
              readOnly: true,
            },
          }}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
